import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../contexts/UserContext';
import { isValidPassword } from '../../lib/passwordRules';
import PasswordHints from '../common/PasswordHints';

const glassCard = {
  background: 'rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.18)',
};

const bgStyle = {
  background: `
    radial-gradient(ellipse at 20% 50%, var(--gradient-warm-1) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, var(--gradient-warm-2) 0%, transparent 40%),
    radial-gradient(ellipse at 50% 80%, var(--gradient-warm-3) 0%, transparent 45%),
    radial-gradient(ellipse at 70% 60%, var(--gradient-warm-4) 0%, transparent 50%),
    var(--gradient-base)
  `,
  backgroundSize: '200% 200%',
  animation: 'splashGradientShift 12s ease infinite',
};

const ResetPasswordScreen = () => {
  const navigate = useNavigate();
  const { session, loading } = useUser();
  const [status, setStatus] = useState('loading'); // loading | form | success | error
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // UserContext already calls getSession() on mount and sets session + loading.
  // By the time loading is false, the recovery session is established (or absent).
  useEffect(() => {
    if (loading) return;
    setStatus(prev => prev === 'success' ? 'success' : (session ? 'form' : 'error'));
  }, [loading, session]);

  const handleSubmit = async () => {
    if (!isValidPassword(password)) return;
    setError('');
    setSubmitting(true);
    const { error } = await supabase.auth.updateUser({ password });
    setSubmitting(false);
    if (error) {
      setError('Something went wrong. Please try again.');
    } else {
      setStatus('success');
      setTimeout(() => navigate('/feed', { replace: true }), 2000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-4" style={bgStyle}>
      <div className="w-full max-w-sm splash-entrance">
        {status === 'loading' && (
          <div className="rounded-2xl p-8 text-center" style={glassCard}>
            <span className="w-8 h-8 border-4 rounded-full animate-spin mx-auto block mb-4"
              style={{ borderColor: 'rgba(255,255,255,0.2)', borderTopColor: '#fff' }} />
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>Verifying link...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-2xl p-8 text-center" style={glassCard}>
            <h2 className="text-xl font-bold text-white mb-2">Link invalid or expired</h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.65)' }}>
              This reset link has already been used or has expired. Request a new one.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Request new link
            </button>
          </div>
        )}

        {status === 'form' && (
          <>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">New password</h1>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                Choose a strong password for your account.
              </p>
            </div>

            <div className="rounded-2xl p-6 space-y-4 mb-4" style={glassCard}>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  New password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="new-password"
                    autoFocus
                    aria-describedby="password-hints"
                    className="w-full px-4 py-3 pr-12 rounded-xl text-base focus:ring-2 focus:outline-none"
                    style={{
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      color: '#fff',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 transition-all active:scale-95"
                    style={{ color: 'rgba(255,255,255,0.5)' }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <PasswordHints id="password-hints" password={password} />
              </div>

              {error && (
                <p className="text-sm text-center" style={{ color: '#F87171' }}>
                  {error}
                </p>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !isValidPassword(password)}
              className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:hover:brightness-100 flex items-center justify-center gap-2"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              {submitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                'Set New Password'
              )}
            </button>
          </>
        )}

        {status === 'success' && (
          <div className="rounded-2xl p-8 text-center" style={glassCard}>
            <h2 className="text-xl font-bold text-white mb-2">Password updated!</h2>
            <p className="text-sm mb-6" style={{ color: 'rgba(255,255,255,0.65)' }}>
              Your password has been changed. You're now signed in.
            </p>
            <button
              onClick={() => navigate('/feed', { replace: true })}
              className="w-full py-3 rounded-xl font-semibold text-white text-sm transition-all hover:brightness-110 active:scale-[0.98]"
              style={{ backgroundColor: 'var(--brand-primary)' }}
            >
              Go to Feed
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPasswordScreen;
